// Small pure helpers for the calendar page — kept out of the JSX file so we
// can unit-test / reason about them independently.

export const WEEKDAYS_TR = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cts", "Paz"];
export const MONTHS_TR = [
  "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
  "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık",
];

export function pad(n) { return String(n).padStart(2, "0"); }

export function isoDate(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function parseIsoDate(s) {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
}

export function addDays(dateOrIso, delta) {
  const d = typeof dateOrIso === "string" ? parseIsoDate(dateOrIso) : new Date(dateOrIso);
  d.setDate(d.getDate() + delta);
  return d;
}

// Monday of the week containing d (Turkish/EU convention).
export function startOfWeek(d) {
  const nd = new Date(d);
  const day = nd.getDay(); // 0=Sun..6=Sat
  const diff = (day + 6) % 7; // Mon=0
  nd.setDate(nd.getDate() - diff);
  nd.setHours(0, 0, 0, 0);
  return nd;
}

// Returns a 6-row × 7-col matrix (42 days) covering the month view, starting Mon.
export function monthGrid(year, month /* 0-based */) {
  const first = new Date(year, month, 1);
  const gridStart = startOfWeek(first);
  const weeks = [];
  for (let w = 0; w < 6; w++) {
    const row = [];
    for (let dCol = 0; dCol < 7; dCol++) {
      row.push(addDays(gridStart, w * 7 + dCol));
    }
    weeks.push(row);
  }
  return { gridStart, weeks };
}

// Split a sorted list of YYYY-MM-DD into consecutive-day runs.
export function splitConsecutive(sortedDates) {
  const runs = [];
  for (const d of sortedDates) {
    if (!runs.length) { runs.push([d]); continue; }
    const last = runs[runs.length - 1];
    const nextIso = isoDate(addDays(last[last.length - 1], 1));
    if (d === nextIso) last.push(d);
    else runs.push([d]);
  }
  return runs;
}

// Given entries + a week (array of 7 Date objects), returns segments to draw.
// A segment is a consecutive run of an entry's dates clipped to the week.
// Segments are lane-assigned via greedy interval scheduling.
export function computeWeekSegments(entries, weekDates) {
  const weekIso = weekDates.map(isoDate);
  const weekStart = weekIso[0];
  const weekEnd = weekIso[6];

  const segs = [];
  for (const entry of entries) {
    const dates = (entry.scheduled_dates || []).slice().sort();
    if (!dates.length) continue;
    const runs = splitConsecutive(dates);
    for (const run of runs) {
      const runStart = run[0];
      const runEnd = run[run.length - 1];
      if (runEnd < weekStart || runStart > weekEnd) continue;
      const clipStart = runStart < weekStart ? weekStart : runStart;
      const clipEnd = runEnd > weekEnd ? weekEnd : runEnd;
      const startCol = weekIso.indexOf(clipStart);
      const endCol = weekIso.indexOf(clipEnd);
      const continuesLeft = runStart < weekStart;
      const continuesRight = runEnd > weekEnd;
      segs.push({
        entry,
        startCol,
        endCol,
        length: endCol - startCol + 1,
        continuesLeft,
        continuesRight,
      });
    }
  }

  // Sort so multi-day blocks bubble up: longer bars first, then earlier start,
  // then id for stability.
  segs.sort((a, b) =>
    (b.length - a.length)
    || (a.startCol - b.startCol)
    || (a.entry.id > b.entry.id ? 1 : -1)
  );

  // Greedy lane assignment (interval scheduling on discrete columns).
  const lanes = []; // lanes[i] = last endCol used
  for (const seg of segs) {
    let placed = false;
    for (let i = 0; i < lanes.length; i++) {
      if (lanes[i] < seg.startCol) {
        seg.lane = i;
        lanes[i] = seg.endCol;
        placed = true;
        break;
      }
    }
    if (!placed) {
      seg.lane = lanes.length;
      lanes.push(seg.endCol);
    }
  }
  return segs;
}

// Deterministic HSL color from a string.
export function colorFor(seed) {
  let h = 0;
  const s = String(seed || "");
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  const hue = h % 360;
  return {
    bg: `hsl(${hue} 78% 92%)`,
    bgHover: `hsl(${hue} 72% 86%)`,
    border: `hsl(${hue} 55% 62%)`,
    text: `hsl(${hue} 40% 25%)`,
    accent: `hsl(${hue} 65% 45%)`,
  };
}

export function colorForEntry(entry) {
  // Group segments by project+stage so the same "kind of task" has one hue;
  // fall back to entry id for free jobs.
  const key = entry.project_id
    ? `${entry.project_id}::${entry.stage || ""}`
    : entry.id;
  return colorFor(key);
}
